import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';
import { SCOPE } from 'src/constants/eventTemplates';

// CON-1487 Phase E — Event Templates actions.

// ── Action Types ──

export const GET_EVENT_TEMPLATES = '@event-templates/get-list';
export const GET_EVENT_TEMPLATES_SUCCESS = '@event-templates/get-list-success';
export const GET_EVENT_TEMPLATES_ERROR = '@event-templates/get-list-error';
export const CLEAR_EVENT_TEMPLATES = '@event-templates/clear-list';

export const UPSERT_EVENT_TEMPLATE = '@event-templates/upsert';
export const UPSERT_EVENT_TEMPLATE_SUCCESS = '@event-templates/upsert-success';
export const UPSERT_EVENT_TEMPLATE_ERROR = '@event-templates/upsert-error';

export const DELETE_EVENT_TEMPLATE = '@event-templates/delete';
export const DELETE_EVENT_TEMPLATE_SUCCESS = '@event-templates/delete-success';
export const DELETE_EVENT_TEMPLATE_ERROR = '@event-templates/delete-error';

export const SET_EVENT_TEMPLATE_SCOPE = '@event-templates/set-scope';
export const CLEAR_EVENT_TEMPLATE_SCOPE = '@event-templates/clear-scope';

// CON-1487 Phase 5 Step 10 — Host Can Invite Via Consultant Text toggle
// save lifecycle. Ephemeral state lives on the local toggle component
// (optimistic UI); these action types drive the saving flag + toast.
export const UPDATE_HOST_CAN_INVITE = '@event-templates/update-host-can-invite';
export const UPDATE_HOST_CAN_INVITE_SUCCESS = '@event-templates/update-host-can-invite-success';
export const UPDATE_HOST_CAN_INVITE_ERROR = '@event-templates/update-host-can-invite-error';

// After a successful toggle save we dispatch one of these so the
// relevant detail slice stays in sync with the DB. Without it,
// HostCanInviteToggle's optimistic-UI rollback (setOptimisticValue(null)
// on success) reads the stale Redux value and visually reverts the
// switch even though the server accepted the change.
export const PATCH_PARTY_TYPE_HOST_CAN_INVITE = '@event-templates/patch-party-type-host-can-invite';
export const PATCH_PARTY_HOST_CAN_INVITE = '@event-templates/patch-party-host-can-invite';

// Three-toggle send methods — combined save lifecycle for the new
// "Allowable Send Methods" card. The card holds local state for all
// three toggles and saves them in one batch. Cascade prompt fires once
// per save (event-type scope only); event scope saves immediately.
export const UPDATE_SEND_METHODS = '@event-templates/update-send-methods';
export const UPDATE_SEND_METHODS_SUCCESS = '@event-templates/update-send-methods-success';
export const UPDATE_SEND_METHODS_ERROR = '@event-templates/update-send-methods-error';

// Patch the relevant detail slice after a successful save so the card
// reads fresh values when it re-renders. Same pattern as the existing
// PATCH_PARTY_(TYPE_)HOST_CAN_INVITE actions.
export const PATCH_PARTY_TYPE_SEND_METHODS = '@event-templates/patch-party-type-send-methods';
export const PATCH_PARTY_SEND_METHODS = '@event-templates/patch-party-send-methods';

// ── Action Creators ──

/** *************************************************************************
 * setEventTemplateScope
 *
 * Records the current scope (partyType or party + scopeId) for the
 * Event Templates list view. Used so the detail screen knows where to
 * return on back-click without a separate lookup.
 *************************************************************************** */
export const setEventTemplateScope = (scopeType, scopeId) => ({
  type: SET_EVENT_TEMPLATE_SCOPE,
  payload: { scopeType, scopeId: scopeId ? Number(scopeId) : null },
});

export const clearEventTemplateScope = () => ({
  type: CLEAR_EVENT_TEMPLATE_SCOPE,
});

/** *************************************************************************
 * getEventTemplatesForPartyType
 *
 * GET /api/eventTemplates/partyType/:partyTypeId
 *************************************************************************** */
export const getEventTemplatesForPartyType = (partyTypeId) => async (dispatch) => {
  dispatch({ type: GET_EVENT_TEMPLATES });
  try {
    const response = await axios.get(`/api/eventTemplates/partyType/${partyTypeId}`);
    dispatch({
      type: GET_EVENT_TEMPLATES_SUCCESS,
      payload: {
        scopeType: SCOPE.PARTY_TYPE,
        scopeId: Number(partyTypeId),
        results: response?.data?.results || [],
      },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      GET_EVENT_TEMPLATES_ERROR,
      helpers.transform.toString(message || error || 'Could not load Event Templates'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: GET_EVENT_TEMPLATES_ERROR });
  }
};

/** *************************************************************************
 * getEventTemplatesForParty
 *
 * GET /api/eventTemplates/party/:partyId
 *************************************************************************** */
export const getEventTemplatesForParty = (partyId) => async (dispatch) => {
  dispatch({ type: GET_EVENT_TEMPLATES });
  try {
    const response = await axios.get(`/api/eventTemplates/party/${partyId}`);
    dispatch({
      type: GET_EVENT_TEMPLATES_SUCCESS,
      payload: {
        scopeType: SCOPE.PARTY,
        scopeId: Number(partyId),
        results: response?.data?.results || [],
      },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      GET_EVENT_TEMPLATES_ERROR,
      helpers.transform.toString(message || error || 'Could not load Event Templates'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: GET_EVENT_TEMPLATES_ERROR });
  }
};

/** *************************************************************************
 * upsertEventTemplate
 *
 * PUT /api/eventTemplates/user
 *
 * `cascadeToOpenParties` is honored by the backend only when
 * scopeType = 'partyType' (it's ignored at party scope).
 *************************************************************************** */
export const upsertEventTemplate = (
  {
    templateType,
    scopeType,
    scopeId,
    subject,
    message,
    attachment,
    attachmentContainer,
    additionalAttachments,
    cascadeToOpenParties,
    categoryId,
    channel,
    label,
    dayStartOffset,
    dayEndOffset,
    active,
  },
  doWhenTry = () => {},
  doWhenCatch = () => {},
) => async (dispatch) => {
  dispatch({ type: UPSERT_EVENT_TEMPLATE });
  try {
    const response = await axios.put('/api/eventTemplates/user', {
      // templateType is omitted for a custom create (server mints the id);
      // categoryId / channel / label are sent only for custom templates.
      ...(templateType !== undefined ? { templateType } : {}),
      scopeType,
      scopeId,
      subject,
      message,
      attachment,
      attachmentContainer,
      // CON-1936 — files 2..N, sent only in the beta (absent otherwise).
      ...(additionalAttachments !== undefined ? { additionalAttachments } : {}),
      cascadeToOpenParties: !!cascadeToOpenParties,
      ...(categoryId !== undefined ? { categoryId } : {}),
      ...(channel !== undefined ? { channel } : {}),
      ...(label !== undefined ? { label } : {}),
      // Per-template day-offset window. Sent only when provided; null is a
      // valid value (clears the template's own window -> inherit category).
      ...(dayStartOffset !== undefined ? { dayStartOffset } : {}),
      ...(dayEndOffset !== undefined ? { dayEndOffset } : {}),
      // Per-template Active (available to the host) toggle.
      ...(active !== undefined ? { active } : {}),
    });
    dispatch({
      type: UPSERT_EVENT_TEMPLATE_SUCCESS,
      payload: response?.data || {},
    });
    dispatch(addNotification(
      UPSERT_EVENT_TEMPLATE_SUCCESS,
      'Template saved',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    doWhenTry(response?.data || {});
  } catch (e) {
    const { error: errMsg, message: respMessage } = e?.response?.data || {};
    dispatch(addNotification(
      UPSERT_EVENT_TEMPLATE_ERROR,
      helpers.transform.toString(respMessage || errMsg || 'Could not save template'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPSERT_EVENT_TEMPLATE_ERROR });
    doWhenCatch(e);
  }
};

/** *************************************************************************
 * deleteEventTemplate
 *
 * DELETE /api/eventTemplates/user/:id
 *
 * Custom templates only (the server rejects system templates). When
 * deleting at event-type scope, `cascadeToOpenParties` also removes the
 * matching copy from open events; it is ignored at event scope.
 *************************************************************************** */
export const deleteEventTemplate = (
  { id, cascadeToOpenParties },
  doWhenTry = () => {},
  doWhenCatch = () => {},
) => async (dispatch) => {
  dispatch({ type: DELETE_EVENT_TEMPLATE });
  try {
    const response = await axios.delete(`/api/eventTemplates/user/${id}`, {
      data: { cascadeToOpenParties: !!cascadeToOpenParties },
    });
    dispatch({ type: DELETE_EVENT_TEMPLATE_SUCCESS, payload: response?.data || {} });
    dispatch(addNotification(
      DELETE_EVENT_TEMPLATE_SUCCESS,
      'Template deleted',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    doWhenTry(response?.data || {});
  } catch (e) {
    const { error: errMsg, message: respMessage } = e?.response?.data || {};
    dispatch(addNotification(
      DELETE_EVENT_TEMPLATE_ERROR,
      helpers.transform.toString(respMessage || errMsg || 'Could not delete template'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: DELETE_EVENT_TEMPLATE_ERROR });
    doWhenCatch(e);
  }
};

export const clearEventTemplates = () => ({ type: CLEAR_EVENT_TEMPLATES });

/** *************************************************************************
 * updateHostCanInviteForPartyType
 *
 * CON-1487 Phase 5 Step 10 — save the toggle at Event Type scope.
 * Reuses the existing PUT /api/partySettings/partytypes endpoint with
 * a partial body + the Phase F cascade flag. Auto-save; optimistic UI
 * lives on the component, which calls onSuccess / onError to roll
 * local state forward or back.
 *************************************************************************** */
export const updateHostCanInviteForPartyType = (
  {
    partyTypeId,
    hostCanInviteViaConsultantSMS,
    cascadeToOpenEvents,
  },
  onSuccess = () => {},
  onError = () => {},
) => async (dispatch) => {
  dispatch({ type: UPDATE_HOST_CAN_INVITE });
  try {
    await axios.put('/api/partySettings/partytypes', {
      partyTypeChanges: {
        id: partyTypeId,
        hostCanInviteViaConsultantSMS,
      },
      updatePartiesHostCanInviteViaConsultantSMS: !!cascadeToOpenEvents,
    });
    // Patch the partyType slice so HostCanInviteToggle reads the
    // fresh value after its optimistic overlay clears.
    dispatch({
      type: PATCH_PARTY_TYPE_HOST_CAN_INVITE,
      payload: { hostCanInviteViaConsultantSMS },
    });
    dispatch({
      type: UPDATE_HOST_CAN_INVITE_SUCCESS,
      payload: { hostCanInviteViaConsultantSMS },
    });
    dispatch(addNotification(
      UPDATE_HOST_CAN_INVITE_SUCCESS,
      hostCanInviteViaConsultantSMS
        ? 'Host Can Invite Via Consultant Text is ON'
        : 'Host Can Invite Via Consultant Text is OFF',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    onSuccess();
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_HOST_CAN_INVITE_ERROR,
      helpers.transform.toString(message || error || 'Could not save toggle'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPDATE_HOST_CAN_INVITE_ERROR });
    onError(e);
  }
};

/** *************************************************************************
 * updateHostCanInviteForParty
 *
 * CON-1487 Phase 5 Step 10 — save the toggle at Event scope. Uses the
 * dedicated narrow PATCH endpoint (no cascade; parties are terminal).
 *************************************************************************** */
export const updateHostCanInviteForParty = (
  { partyId, hostCanInviteViaConsultantSMS },
  onSuccess = () => {},
  onError = () => {},
) => async (dispatch) => {
  dispatch({ type: UPDATE_HOST_CAN_INVITE });
  try {
    await axios.patch(
      `/api/parties/${partyId}/host-can-invite`,
      { hostCanInviteViaConsultantSMS },
    );
    // Patch the party slice so HostCanInviteToggle reads the fresh
    // value after its optimistic overlay clears.
    dispatch({
      type: PATCH_PARTY_HOST_CAN_INVITE,
      payload: { hostCanInviteViaConsultantSMS },
    });
    dispatch({
      type: UPDATE_HOST_CAN_INVITE_SUCCESS,
      payload: { hostCanInviteViaConsultantSMS },
    });
    dispatch(addNotification(
      UPDATE_HOST_CAN_INVITE_SUCCESS,
      hostCanInviteViaConsultantSMS
        ? 'Host Can Invite Via Consultant Text is ON'
        : 'Host Can Invite Via Consultant Text is OFF',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    onSuccess();
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_HOST_CAN_INVITE_ERROR,
      helpers.transform.toString(message || error || 'Could not save toggle'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPDATE_HOST_CAN_INVITE_ERROR });
    onError(e);
  }
};

/** *************************************************************************
 * updateSendMethodsForPartyType
 *
 * Three-toggle send methods — combined save at Event Type scope. Sends
 * a partial trio (only the fields that changed) plus the cascade flag.
 * Backend's wouldLeaveAllSendMethodsOff helper rejects if the result
 * would leave all three off; we surface that as an error toast.
 *************************************************************************** */
export const updateSendMethodsForPartyType = (
  {
    partyTypeId,
    hostCanInviteViaConsultantSMS,
    hostHasTextingOptions,
    hostHasEmailOptions,
    hostCanInviteViaOtherApp,
    hostWelcomeVideoUrl,
    cascadeToOpenEvents,
  },
  onSuccess = () => {},
  onError = () => {},
) => async (dispatch) => {
  dispatch({ type: UPDATE_SEND_METHODS });
  try {
    const partyTypeChanges = { id: partyTypeId };
    if (hostCanInviteViaConsultantSMS !== undefined) {
      partyTypeChanges.hostCanInviteViaConsultantSMS = !!hostCanInviteViaConsultantSMS;
    }
    if (hostHasTextingOptions !== undefined) {
      partyTypeChanges.hostHasTextingOptions = !!hostHasTextingOptions;
    }
    if (hostHasEmailOptions !== undefined) {
      partyTypeChanges.hostHasEmailOptions = !!hostHasEmailOptions;
    }
    if (hostCanInviteViaOtherApp !== undefined) {
      partyTypeChanges.hostCanInviteViaOtherApp = !!hostCanInviteViaOtherApp;
    }
    // CON-1576 — Host Welcome Video URL rides the same band save.
    // Empty string clears the URL (server stores NULL), which means no
    // welcome-video row on the event — not a fallback to a default video.
    const normalizedWelcomeVideoUrl = (typeof hostWelcomeVideoUrl === 'string'
      && hostWelcomeVideoUrl.trim()) || null;
    if (hostWelcomeVideoUrl !== undefined) {
      partyTypeChanges.hostWelcomeVideoUrl = normalizedWelcomeVideoUrl;
    }
    await axios.put('/api/partySettings/partytypes', {
      partyTypeChanges,
      updatePartiesSendMethods: !!cascadeToOpenEvents,
      // The server only cascades the URL when it was supplied in this
      // save, so the flag is safe to pass whenever the user said Yes.
      updatePartiesHostWelcomeVideoUrl: !!cascadeToOpenEvents,
    });
    // Patch only the fields that were actually saved so the partyType
    // slice stays in sync with the DB.
    const sliceUpdate = {};
    if (hostCanInviteViaConsultantSMS !== undefined) {
      sliceUpdate.hostCanInviteViaConsultantSMS = !!hostCanInviteViaConsultantSMS;
    }
    if (hostHasTextingOptions !== undefined) {
      sliceUpdate.hostHasTextingOptions = !!hostHasTextingOptions;
    }
    if (hostHasEmailOptions !== undefined) {
      sliceUpdate.hostHasEmailOptions = !!hostHasEmailOptions;
    }
    if (hostCanInviteViaOtherApp !== undefined) {
      sliceUpdate.hostCanInviteViaOtherApp = !!hostCanInviteViaOtherApp;
    }
    if (hostWelcomeVideoUrl !== undefined) {
      sliceUpdate.hostWelcomeVideoUrl = normalizedWelcomeVideoUrl;
    }
    dispatch({
      type: PATCH_PARTY_TYPE_SEND_METHODS,
      payload: sliceUpdate,
    });
    dispatch({
      type: UPDATE_SEND_METHODS_SUCCESS,
      payload: sliceUpdate,
    });
    dispatch(addNotification(
      UPDATE_SEND_METHODS_SUCCESS,
      'Send methods saved',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    onSuccess();
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_SEND_METHODS_ERROR,
      helpers.transform.toString(message || error || 'Could not save send methods'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPDATE_SEND_METHODS_ERROR });
    onError(e);
  }
};

/** *************************************************************************
 * updateSendMethodsForParty
 *
 * Four-toggle send methods — combined save at Event scope. Uses the
 * dedicated narrow PATCH endpoint (no cascade; parties are terminal).
 * The endpoint requires all four fields in the body.
 *************************************************************************** */
export const updateSendMethodsForParty = (
  {
    partyId,
    hostCanInviteViaConsultantSMS,
    hostHasTextingOptions,
    hostHasEmailOptions,
    hostCanInviteViaOtherApp,
  },
  onSuccess = () => {},
  onError = () => {},
) => async (dispatch) => {
  dispatch({ type: UPDATE_SEND_METHODS });
  try {
    await axios.patch(
      `/api/parties/${partyId}/send-methods`,
      {
        hostCanInviteViaConsultantSMS: !!hostCanInviteViaConsultantSMS,
        hostHasTextingOptions: !!hostHasTextingOptions,
        hostHasEmailOptions: !!hostHasEmailOptions,
        hostCanInviteViaOtherApp: !!hostCanInviteViaOtherApp,
      },
    );
    const sliceUpdate = {
      hostCanInviteViaConsultantSMS: !!hostCanInviteViaConsultantSMS,
      hostHasTextingOptions: !!hostHasTextingOptions,
      hostHasEmailOptions: !!hostHasEmailOptions,
      hostCanInviteViaOtherApp: !!hostCanInviteViaOtherApp,
    };
    dispatch({
      type: PATCH_PARTY_SEND_METHODS,
      payload: sliceUpdate,
    });
    dispatch({
      type: UPDATE_SEND_METHODS_SUCCESS,
      payload: sliceUpdate,
    });
    dispatch(addNotification(
      UPDATE_SEND_METHODS_SUCCESS,
      'Send methods saved',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    onSuccess();
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_SEND_METHODS_ERROR,
      helpers.transform.toString(message || error || 'Could not save send methods'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPDATE_SEND_METHODS_ERROR });
    onError(e);
  }
};

/** *************************************************************************
 * updateHostWelcomeVideoForParty
 *
 * CON-1576 — Host Welcome Video URL save at Event scope. Uses its own
 * narrow PATCH endpoint (no cascade; parties are terminal) so it fires
 * independently of the send-method booleans. Empty string clears back
 * the URL (server stores NULL), which means no welcome-video row on the
 * event rather than a fallback to a default video.
 *************************************************************************** */
export const updateHostWelcomeVideoForParty = (
  {
    partyId,
    hostWelcomeVideoUrl,
  },
  onSuccess = () => {},
  onError = () => {},
) => async (dispatch) => {
  dispatch({ type: UPDATE_SEND_METHODS });
  try {
    const normalizedWelcomeVideoUrl = (typeof hostWelcomeVideoUrl === 'string'
      && hostWelcomeVideoUrl.trim()) || null;
    await axios.patch(
      `/api/parties/${partyId}/host-welcome-video`,
      { hostWelcomeVideoUrl: normalizedWelcomeVideoUrl },
    );
    const sliceUpdate = { hostWelcomeVideoUrl: normalizedWelcomeVideoUrl };
    dispatch({
      type: PATCH_PARTY_SEND_METHODS,
      payload: sliceUpdate,
    });
    dispatch({
      type: UPDATE_SEND_METHODS_SUCCESS,
      payload: sliceUpdate,
    });
    dispatch(addNotification(
      UPDATE_SEND_METHODS_SUCCESS,
      'Host welcome video saved',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    onSuccess();
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_SEND_METHODS_ERROR,
      helpers.transform.toString(message || error || 'Could not save the host welcome video'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPDATE_SEND_METHODS_ERROR });
    onError(e);
  }
};
